'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdAttachMoney,
    MdCalendarToday,
    MdCheckCircle,
    MdEdit,
    MdLock,
    MdLockOpen,
    MdRepeat,
    MdStar,
    MdViewList,
    MdWidgets,
} from 'react-icons/md'
import {
    formatPlanPrice,
    getPlan,
    getPlanRegistrationLinks,
    createPlanRegistrationLink,
    revokePlanRegistrationLink,
    Plan,
    RECURRENCE_LABELS,
} from '@/lib/api/plans/plan-api'
import RegistrationLinksPanel from '@/components/registration-links/registration-links-panel'
import { getSystemModules, SystemModule } from '@/lib/api/system-modules/system-modules-api'

export default function PlanDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [plan, setPlan] = useState<Plan | null>(null)
    const [systemModules, setSystemModules] = useState<SystemModule[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (id) {
            loadPlan()
            loadSystemModules()
        }
    }, [id])

    const loadSystemModules = async () => {
        try {
            const response = await getSystemModules()
            if (response.success && response.result) {
                setSystemModules(response.result)
            }
        } catch {
            // silently fail
        }
    }

    const loadPlan = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getPlan(parseInt(id, 10))
            if (response.success && response.result) {
                setPlan(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar plano')
                navigate('/plans')
            }
        } catch {
            toast.error('Erro ao carregar plano')
            navigate('/plans')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do plano...
                </p>
            </div>
        )
    }

    if (!plan) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdViewList className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Plano não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O plano que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/plans')}
                    className="btn btn-secondary"
                >
                    Voltar para Planos
                </button>
            </div>
        )
    }

    const features = plan.features
        ? Object.entries(plan.features)
        : []

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/plans')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 items-center justify-center shadow-lg">
                            <MdViewList className="text-2xl text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                    {plan.name}
                                </h1>
                                <span className="badge badge-sm badge-secondary badge-outline">
                                    {RECURRENCE_LABELS[plan.recurrence]}
                                </span>
                                {plan.private && (
                                    <span className="badge badge-sm badge-warning gap-1">
                                        <MdLock className="text-xs" />
                                        Privado
                                    </span>
                                )}
                                {plan.isCustom && (
                                    <span className="badge badge-sm badge-secondary gap-1">
                                        <MdStar className="text-xs" />
                                        Sob Medida
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-base-content/60 mt-1">
                                Detalhes do plano
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(`/plans/edit/${plan.id}`)}
                        className="btn btn-secondary gap-2"
                    >
                        <MdEdit className="text-lg" />
                        <span className="hidden sm:inline">Editar</span>
                    </button>
                </div>
            </div>

            {/* Card Principal */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                {/* Datas */}
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                Criado em{' '}
                                {format(
                                    new Date(plan.createdAt),
                                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR },
                                )}
                            </span>
                        </div>
                        {plan.updatedAt !== plan.createdAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <span>
                                    Atualizado em{' '}
                                    {format(
                                        new Date(plan.updatedAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR },
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {/* Descrição */}
                    <p className="text-base-content/70 mb-6">
                        {plan.description}
                    </p>

                    {/* Dados Gerais */}
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <MdAttachMoney className="text-secondary" />
                        Valores e Configurações
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                            <div className="p-2 bg-success/10 rounded-lg">
                                <MdAttachMoney className="text-xl text-success" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Preço
                                </p>
                                <p className="text-xl font-bold text-success">
                                    {formatPlanPrice(plan.price)}
                                    <span className="text-sm font-normal text-base-content/50">
                                        /{RECURRENCE_LABELS[plan.recurrence].toLowerCase().slice(0, 3)}
                                    </span>
                                </p>
                            </div>
                        </div>

                        {plan.isCustom && plan.customPrice != null && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdStar className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Preço Sob Medida
                                    </p>
                                    <p className="text-xl font-bold text-secondary">
                                        {formatPlanPrice(plan.customPrice)}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                            <div className="p-2 bg-info/10 rounded-lg">
                                <MdRepeat className="text-xl text-info" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Recorrência
                                </p>
                                <p className="text-sm font-semibold text-base-content">
                                    {RECURRENCE_LABELS[plan.recurrence]}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                            <div className="p-2 bg-warning/10 rounded-lg">
                                {plan.private ? (
                                    <MdLock className="text-xl text-warning" />
                                ) : (
                                    <MdLockOpen className="text-xl text-success" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Visibilidade
                                </p>
                                <p className="text-sm font-semibold text-base-content">
                                    {plan.private ? 'Privado' : 'Público'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Links de Cadastro: vários links vivos ao mesmo tempo, um
                        por cliente. Ver RegistrationLinksPanel. */}
                    {id && (
                        <RegistrationLinksPanel
                            defaultTrialDays={plan?.trialDays ?? null}
                            onList={() =>
                                getPlanRegistrationLinks(parseInt(id, 10))
                            }
                            onCreate={(data) =>
                                createPlanRegistrationLink(parseInt(id, 10), data)
                            }
                            onRevoke={(linkId) =>
                                revokePlanRegistrationLink(
                                    parseInt(id, 10),
                                    linkId,
                                )
                            }
                        />
                    )}

                    {/* Features */}
                    {features.length > 0 && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                                <MdCheckCircle className="text-success" />
                                Funcionalidades
                                <span className="badge badge-sm badge-secondary">
                                    {features.length}
                                </span>
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {features.map(([key, value]) => (
                                    <div
                                        key={key}
                                        className="flex items-center gap-3 p-3 bg-base-200/30 rounded-xl"
                                    >
                                        <MdCheckCircle className="text-success flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium text-base-content">
                                                {key}
                                            </span>
                                        </div>
                                        <span className="text-xs text-base-content/50 font-mono bg-base-200/50 px-2 py-1 rounded">
                                            {String(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Tier e Módulos */}
                    <div className="mt-6">
                        <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                            <MdWidgets className="text-secondary" />
                            Tier e Módulos
                        </h3>

                        {/* Tier */}
                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl mb-4">
                            <div className="p-2 bg-secondary/10 rounded-lg">
                                <MdStar className="text-xl text-secondary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Tier Base
                                </p>
                                <p className="text-sm font-semibold text-base-content">
                                    {plan.tier ? plan.tier.name : 'Nenhum'}
                                </p>
                            </div>
                        </div>

                        {/* Módulos Adicionais */}
                        {plan.additionalModules && typeof plan.additionalModules === 'object' && Object.keys(plan.additionalModules).length > 0 && (
                            <div className="mb-4">
                                <p className="text-xs font-semibold text-success uppercase tracking-wider mb-2">
                                    Módulos Adicionais ({Object.keys(plan.additionalModules).length})
                                </p>
                                <div className="space-y-2">
                                    {Object.entries(plan.additionalModules as Record<string, string[]>).map(([moduleKey, resources]) => {
                                        const sysMod = systemModules.find(m => m.key === moduleKey)
                                        const moduleLabel = sysMod?.label || moduleKey
                                        const allResources = resources.length === 0

                                        return (
                                            <div key={moduleKey} className="p-3 bg-success/5 border border-success/20 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <MdCheckCircle className="text-success flex-shrink-0" />
                                                    <span className="text-sm font-semibold text-base-content">
                                                        {moduleLabel}
                                                    </span>
                                                    <span className="badge badge-outline badge-xs">
                                                        {allResources ? 'Todos' : `${resources.length}`} recursos
                                                    </span>
                                                </div>
                                                {sysMod && sysMod.resources.length > 0 && !allResources && (
                                                    <div className="ml-6 flex flex-wrap gap-1 mt-2">
                                                        {sysMod.resources.map(res => (
                                                            <span
                                                                key={res.key}
                                                                className={`badge badge-sm ${
                                                                    resources.includes(res.key)
                                                                        ? 'badge-success badge-outline'
                                                                        : 'badge-ghost opacity-40 line-through'
                                                                }`}
                                                            >
                                                                {res.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Módulos Excluídos */}
                        {plan.excludedModules && plan.excludedModules.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-error uppercase tracking-wider mb-2">
                                    Módulos Excluídos ({plan.excludedModules.length})
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {plan.excludedModules.map(moduleKey => {
                                        const sysMod = systemModules.find(m => m.key === moduleKey)
                                        return (
                                            <span
                                                key={moduleKey}
                                                className="badge badge-error badge-outline line-through"
                                            >
                                                {sysMod?.label || moduleKey}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
